<!-- ポータルトップ -->
<template>
  <div>
    <HeaderComponent />
    <div v-if="isDataFetched == true" class="home_main_cnt1">
      <CarouselComponent />
      <IconMenuComponent />
    </div>
    <div id="contents1">
      <section class="com_sec1">
        <div class="com_inner1 clearfix">
          <!-- 管理者からのお知らせ -->
          <AnnouncementAreaComponent />
          <!-- お知らせ -->
          <NewsAreaComponent />
          <div class="com_contents1">
            <div class="main_column1">
              <div class="vertical_components">
                <!-- お気に入りページ -->
                <FavoritePageComponent />
                <!-- 最近チェックしたページ -->
                <RecentlyViewedPagesComponent />
              </div>
              <!-- 右側のサイドバナーエリア描画 -->
              <div class="horizontal_component">
                <SideSliderComponent />
              </div>
            </div>
          </div>
          <!-- 関連情報リンク -->
          <RelatedInfoLinksComponent />
        </div>
      </section>
      <!-- ログイン後ポップアップ通知 -->
      <LoginPopupModalComponent
        v-if="isLoginPopupPermission && currentLoginPopup !== null"
        :isOpen="true"
        :isPreview="false"
        :categoryId="currentLoginPopup.categoryId"
        :contentsId="currentLoginPopup.contentsId"
        @closed="handleLoginPopupModalClosed"
      />
    </div>
    <!-- フッタ -->
    <FooterComponent />
  </div>
  <!-- フローティングリンク -->
  <FloatingLinkComponent v-if="isIncentiveEntry && isIncentivePermission" />
  <BackToTop />
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, nextTick } from "vue";
import HeaderComponent from "../layout/HeaderComponent.vue";
import CarouselComponent from "../layout/CarouselComponent.vue";
import IconMenuComponent from "../layout/IconMenuComponent.vue";
import FooterComponent from "../layout/FooterComponent.vue";
import NewsAreaComponent from "../layout/NewsAreaComponent.vue";
import AnnouncementAreaComponent from "../layout/AnnouncementAreaComponent.vue";
import FavoritePageComponent from "../layout/FavoritePageComponent.vue";
import SideSliderComponent from "../layout/SideSliderComponent.vue";
import RecentlyViewedPagesComponent from "../layout/RecentlyViewedPagesComponent.vue";
import RelatedInfoLinksComponent from "../layout/RelatedInfoLinksComponent.vue";
import { useProcessingStore } from "../../store/useProcessingStore";
import BackToTop from "../controls/BackToTop.vue";
import { useUserParmitionStore } from "@/store/userParmitionStore";
import { AppConstants } from "@/constants/AppConstants";
import FloatingLinkComponent from "../layout/FloatingLinkComponent.vue";
import apiAuthClient from "@/util/apiClientAuth";
import { AxiosError } from "axios";
import { outputDebugLogs, formatMessage, getTargetDate } from "@/Common";
import LoginPopupModalComponent from "../layout/LoginPopupModalComponent.vue";
import messages from "@/assets/messages.json";

// 動的コンポーネントのデータ構造を定義
interface DynamicComponent {
  id: string; // 一意の識別子
  name: string; // コンポーネント名
}

// ログイン後ポップアップ通知一覧のデータ構造
interface LoginPopupListData {
  categoryId: string;
  contentsId: string;
}

class PotalTopConstants {
  // API-URL
  // 担当者別ログイン後ポップアップ通知一覧データ取得
  static readonly API_URL_GET_USER_POPUP_LIST =
    "/api/LoginPopup/GetUserPopupList";
}

export default defineComponent({
  name: "PortalTopComponent",
  components: {
    HeaderComponent,
    CarouselComponent,
    IconMenuComponent,
    FooterComponent,
    NewsAreaComponent,
    AnnouncementAreaComponent,
    FavoritePageComponent,
    SideSliderComponent,
    RecentlyViewedPagesComponent,
    RelatedInfoLinksComponent,
    BackToTop,
    FloatingLinkComponent,
    LoginPopupModalComponent,
  },
  setup() {
    const dynamicComponents = ref<DynamicComponent[]>([]);
    const processingStore = useProcessingStore();
    const { getCategoryIdByName, getPermissionFlag } = useUserParmitionStore();
    const isIncentiveEntry = ref(false); // 奨励表彰エントリー状態
    const isIncentivePermission = ref(false); // 奨励金の利用権限
    const isLoginPopupPermission = ref(false); // ログイン後ポップアップ通知の利用権限
    const agCode = ref(""); // AGコード
    const userId = ref(""); // ユーザーID
    const pseudoAgCode = ref(""); // 疑似ログインAGコード
    const pseudoUserId = ref(""); // 疑似ログインユーザーID
    const serchAgCode = ref(""); // 検索用AGコード
    const serchUserId = ref(""); // 検索用ユーザーID
    const loginPopupList = ref<LoginPopupListData[]>([]); // ログイン後ポップアップ通知一覧
    const currentLoginPopup = ref<LoginPopupListData | null>(null);
    const isDataFetched = ref(false); // データが取得済みかどうかを追跡
    // 初期処理
    onMounted(async () => {
      // ローカルストレージからユーザー情報を取得して内部変数に展開
      const userInfo = localStorage.getItem(AppConstants.USER_SESSION_NAME);
      if (userInfo) {
        const parsedUserInfo = JSON.parse(userInfo);
        agCode.value = parsedUserInfo.agCode;
        userId.value = parsedUserInfo.userId;
        pseudoAgCode.value = parsedUserInfo.pseudoAgCode;
        pseudoUserId.value = parsedUserInfo.pseudoUserId;
      }

      if (pseudoUserId.value == "") {
        // 疑似ログインIDが空の場合は、疑似ログインしていないとみなす
        serchAgCode.value = agCode.value;
        serchUserId.value = userId.value;
      } else {
        outputDebugLogs("疑似ログイン状態");
        serchAgCode.value = pseudoAgCode.value;
        serchUserId.value = pseudoUserId.value;
      }

      // 利用権限取得
      fetchIncentivePermission(); // 奨励金
      fetchLoginPopupPermission(); // ログイン後ポップアップ通知

      // 担当者別ログイン後ポップアップ通知一覧データ取得
      if (isLoginPopupPermission.value) {
        if (await getUserPopupList()) {
          // ログイン後ポップアップ通知の表示
          showLoginPopupModal();
        }
      }

      // 画像読み込み完了やレイアウト再計算を待つ
      await nextTick();

      // 最後に isDataFetched を true にする
      isDataFetched.value = true;
    });

    /**
     * 奨励金の利用権限取得
     */
    const fetchIncentivePermission = () => {
      // ローカルストレージからインセンティブ情報を取得
      const incentiveInfo = localStorage.getItem(
        AppConstants.INCENTIVE_INFO_SESSION_NAME
      );
      if (incentiveInfo) {
        const parsedIncentiveInfo = JSON.parse(incentiveInfo);
        isIncentiveEntry.value = parsedIncentiveInfo.isIncentiveEntry;
      }

      // カテゴリID取得
      let incentiveCategoryId = getCategoryIdByName(
        AppConstants.CATEGORY_NAME_INCENTIVE
      );

      // 利用権限取得
      if (incentiveCategoryId) {
        isIncentivePermission.value = getPermissionFlag(incentiveCategoryId);
      }
    };

    /**
     * ログイン後ポップアップ通知の利用権限取得
     */
    const fetchLoginPopupPermission = () => {
      // カテゴリID取得
      let categoryId = getCategoryIdByName(
        AppConstants.CATEGORY_NAME_LOGIN_POPUP
      );

      // 利用権限取得
      if (categoryId) {
        isLoginPopupPermission.value = getPermissionFlag(categoryId);
      }
    };

    /**
     * 担当者別ログイン後ポップアップ通知一覧データ取得
     */
    const getUserPopupList = async (): Promise<boolean> => {
      try {
        outputDebugLogs("担当者別ログイン後ポップアップ通知一覧データ取得");

        // APIを実行
        const response = await apiAuthClient.post(
          PotalTopConstants.API_URL_GET_USER_POPUP_LIST,
          {
            systemKbn: AppConstants.SYSTEM_KBN_WITH,
            agCode: serchAgCode.value,
            userId: serchUserId.value,
            targetDate: getTargetDate(""),
            isUnlimitDate: false,
          }
        );

        // レスポンスからポップアップ通知一覧データを取得
        const data = response.data;
        loginPopupList.value = data;

        outputDebugLogs("担当者別ログイン後ポップアップ通知一覧データ取得");
        return true;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        outputDebugLogs("Error: " + axiosError.message);
        alert(
          formatMessage(messages.ERR_SYS003, "ポップアップ通知一覧データの取得")
        );
        return false;
      }
    };

    /**
     * ログイン後ポップアップ通知の表示処理
     */
    const showLoginPopupModal = () => {
      if (loginPopupList.value.length > 0) {
        // 配列の先頭を削除し、削除した要素を取得
        const nextPopup = loginPopupList.value.shift();
        currentLoginPopup.value = nextPopup !== undefined ? nextPopup : null;
        // 画面の縦スクロールを無効化
        document.body.classList.add("modal-open");
      } else {
        currentLoginPopup.value = null;
        // 画面の縦スクロールを有効化
        document.body.classList.remove("modal-open");
      }
    };

    /**
     * ログイン後ポップアップ通知の終了処理
     */
    const handleLoginPopupModalClosed = () => {
      // 次のログイン後ポップアップ通知を表示
      showLoginPopupModal();
    };

    return {
      dynamicComponents,
      isProcessing: processingStore.isProcessing,
      isIncentiveEntry,
      isIncentivePermission,
      isLoginPopupPermission,
      currentLoginPopup,
      handleLoginPopupModalClosed,
      isDataFetched,
    };
  },
});
</script>

<style scoped>
/* ここにスタイリングを追加 */
</style>
